import useMasterData from "./useMasterData";

// Type definitions
type Filters = {
    workstream: number[] | null;
    work_type_group: number[] | null;
};

type FilterItem = {
    id: number;
    name: string;
};

type FilteredData = {
    work_type_groups: FilterItem[];
    work_types: FilterItem[];
};

type ProjectFilterData = {
    workstream_id: number;
    work_type_group_id: number;
    work_type_group_name: string;
    work_type_id: number;
    work_type_name: string;
};

type FormData = {
    workstream?: number[] | number | null;
    work_type_group?: number[] | number | null;
    work_type?: number[] | number | null;
    [key: string]: unknown;
};

type FilterKey = "workstream" | "work_type_group";

/**
 * Custom hook for managing project filters with cascading dependencies
 * Handles workstream → work type group → work type relationships
 */
export const useProjectFilters = () => {
    const { projectsFilterData } = useMasterData();

    /**
     * Extracts unique items by ID and name keys from an array
     */
    const uniqueByKey = <T extends Record<string, unknown>>(
        arr: T[],
        idKey: keyof T,
        nameKey: keyof T
    ): FilterItem[] => {
        const map = new Map<number, FilterItem>();

        arr.forEach((item) => {
            const id = Number(item[idKey]);
            if (!map.has(id)) {
                map.set(id, {
                    id,
                    name: String(item[nameKey]),
                });
            }
        });

        return Array.from(map.values());
    };

    /**
     * Filters project data based on workstream and work type group selections
     * Returns available work type groups and work types based on active filters
     */
    const getFilteredData = (filters: Filters): FilteredData => {
        if (!projectsFilterData?.length) {
            return {
                work_type_groups: [],
                work_types: [],
            };
        }

        // Step 1: Filter by workstream
        const workstreamFiltered = filters.workstream?.length
            ? projectsFilterData.filter((item: ProjectFilterData) =>
                  filters.workstream!.includes(item.workstream_id)
              )
            : projectsFilterData;

        // Step 2: Extract unique work type groups from filtered workstreams
        const workTypeGroups = uniqueByKey(
            workstreamFiltered,
            "work_type_group_id",
            "work_type_group_name"
        );

        // Step 3: Further filter by work type group
        const workTypeGroupFiltered = filters.work_type_group?.length
            ? workstreamFiltered.filter((item: ProjectFilterData) =>
                  filters.work_type_group!.includes(item.work_type_group_id)
              )
            : workstreamFiltered;

        // Step 4: Extract unique work types from filtered results
        const workTypes = uniqueByKey(
            workTypeGroupFiltered,
            "work_type_id",
            "work_type_name"
        );

        return {
            work_type_groups: workTypeGroups,
            work_types: workTypes,
        };
    };

    /**
     * Validates and returns valid record IDs based on current filter state
     */
    const getValidRecords = (
        formData: FormData,
        filterData: ProjectFilterData[]
    ) => {
        const { workstream, work_type_group } = formData;

        const validRecords = filterData.filter((item) => {
            const matchWorkstream =
                !workstream || 
                (Array.isArray(workstream) ? workstream.includes(item.workstream_id) : workstream === item.workstream_id);
            const matchWorkTypeGroup =
                !work_type_group ||
                (Array.isArray(work_type_group) ? work_type_group.includes(item.work_type_group_id) : work_type_group === item.work_type_group_id);

            return matchWorkstream && matchWorkTypeGroup;
        });

        return {
            validWorkTypeGroupIds: validRecords.map((x) => x.work_type_group_id),
            validWorkTypeIds: validRecords.map((x) => x.work_type_id),
        };
    };

    /**
     * Cleans filter selections when a parent filter changes (multi-select forms)
     * Removes invalid downstream selections based on the changed filter
     */
    const cleanFilters = (
        updated: FormData,
        changedKey: FilterKey,
        actions: ProjectFilterData[] = projectsFilterData
    ): FormData => {
        if (!actions?.length) return updated;

        const cleaned = { ...updated };
        const { work_type_group, work_type } = cleaned;

        const { validWorkTypeGroupIds, validWorkTypeIds } = getValidRecords(cleaned, actions);

        // Workstream changed: validate and clean work type groups and reset work types
        if (changedKey === "workstream") {
            cleaned.work_type_group = Array.isArray(work_type_group)
                ? work_type_group.filter((id: number) => validWorkTypeGroupIds.includes(id))
                : null;
            cleaned.work_type = null;
        }

        // Work type group changed: validate and clean work types
        if (changedKey === "work_type_group") {
            cleaned.work_type = Array.isArray(work_type)
                ? work_type.filter((id: number) => validWorkTypeIds.includes(id))
                : null;
        }

        return cleaned;
    };

    /**
     * Cleans filter selections for single-select forms
     * Resets downstream selections if parent selection becomes invalid
     */
    const cleanSingleFormFilters = (
        updated: FormData,
        changedKey: FilterKey,
        actions: ProjectFilterData[] = projectsFilterData
    ): FormData => {
        if (!actions?.length) return updated;

        const cleaned = { ...updated };
        const { workstream, work_type_group, work_type } = cleaned;

        // Filter valid records based on single value selections
        const validRecords = actions.filter((item) => {
            const matchWorkstream = !workstream || item.workstream_id === workstream;
            const matchWorkTypeGroup = !work_type_group || item.work_type_group_id === work_type_group;
            return matchWorkstream && matchWorkTypeGroup;
        });

        // Reset work type group and work type if current selection is invalid
        if (
            work_type_group &&
            !validRecords.some((item) => item.work_type_group_id === work_type_group)
        ) {
            cleaned.work_type_group = null;
            cleaned.work_type = null;
        }

        // Reset work type if current selection is invalid
        if (
            work_type &&
            !validRecords.some((item) => item.work_type_id === work_type)
        ) {
            cleaned.work_type = null;
        }

        return cleaned;
    };

    return {
        projectsFilterData,
        getFilteredData,
        cleanFilters,
        cleanSingleFormFilters,
    };
};